import { BookOpen, X as CloseIcon, Container, Loader2, Terminal, Zap } from "lucide-react";
import React, { useState } from "react";
import { useToast } from "@/components/shared";
import { ApiError } from "@/lib/api";
import { extensionService } from "@/lib/services";
import { ColorPicker, IconPicker } from "./IconColorPickers";

const SKILL_TYPE_OPTIONS = [
  {
    value: "context" as const,
    label: "Context",
    description: "Instructions only — guides the AI without code execution",
    icon: BookOpen,
  },
  {
    value: "dynamic_session" as const,
    label: "Dynamic Session",
    description: "Run Python code in a sandboxed Azure Hyper-V session",
    icon: Terminal,
  },
  {
    value: "custom_container" as const,
    label: "Custom Container",
    description: "Execute in a dedicated Docker container with custom tools",
    icon: Container,
  },
];

export interface AddSkillModalProps {
  extensionUuid: string;
  onClose: () => void;
  onSave: () => void;
}

export function AddSkillModal({ extensionUuid, onClose, onSave }: Readonly<AddSkillModalProps>) {
  const { showToast } = useToast();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [skillType, setSkillType] = useState("context");
  const [instructions, setInstructions] = useState("");
  const [icon, setIcon] = useState("zap");
  const [color, setColor] = useState("#7C16F5");
  const [category, setCategory] = useState("");
  const [tags, setTags] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim() || !instructions.trim()) {
      showToast("Please fill in all required fields", "warning");
      return;
    }

    try {
      setSaving(true);
      const parsedTags = tags
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean);

      await extensionService.addSkill(extensionUuid, {
        name: name.trim(),
        description: description.trim() || undefined,
        skillType,
        instructions: instructions.trim(),
        icon: icon.trim() || undefined,
        color: color.trim() || undefined,
        category: category.trim() || undefined,
        tags: parsedTags.length > 0 ? parsedTags : undefined,
      });
      showToast("Skill added successfully", "success");
      onSave();
    } catch (err) {
      console.error("Failed to add skill:", err);
      // Surface the specific API validation error if available
      if (err instanceof ApiError && err.data) {
        const detail = err.data as { detail?: string | Array<{ msg: string; loc: string[] }> };
        if (typeof detail.detail === "string") {
          showToast(`Failed to add skill: ${detail.detail}`, "error");
        } else if (Array.isArray(detail.detail) && detail.detail.length > 0) {
          const messages = detail.detail.map((d) => `${d.loc.at(-1)}: ${d.msg}`).join(", ");
          showToast(`Validation error — ${messages}`, "error");
        } else {
          showToast("Failed to add skill", "error");
        }
      } else {
        showToast("Failed to add skill", "error");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-surface-overlay flex items-center justify-center z-50 p-4">
      <div className="bg-surface-primary rounded-xl border border-border-primary w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-surface-primary border-b border-border-primary px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-content-primary">Add Skill</h2>
          <button onClick={onClose} className="p-2 hover:bg-interactive-hover rounded-lg transition-colors">
            <CloseIcon className="w-5 h-5 text-content-tertiary" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label htmlFor="add-skill-name" className="block text-sm font-medium text-content-secondary mb-1.5">
              Name <span className="text-red-400">*</span>
            </label>
            <input
              id="add-skill-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="My Custom Skill"
              className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50"
              required
            />
          </div>

          <div>
            <label htmlFor="add-skill-description" className="block text-sm font-medium text-content-secondary mb-1.5">
              Description
            </label>
            <textarea
              id="add-skill-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What does this skill do?"
              rows={2}
              className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 resize-none"
            />
          </div>

          <div>
            <span className="block text-sm font-medium text-content-secondary mb-1.5">Execution Context</span>
            <div className="flex gap-3">
              {SKILL_TYPE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setSkillType(opt.value)}
                  className={`flex-1 flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-colors text-center ${
                    skillType === opt.value
                      ? "border-brand-primary bg-brand-primary/5"
                      : "border-border-secondary bg-surface-secondary hover:border-content-quaternary"
                  }`}
                >
                  <opt.icon
                    className={`w-6 h-6 ${skillType === opt.value ? "text-brand-primary" : "text-content-tertiary"}`}
                  />
                  <div className="font-medium text-sm text-content-primary">{opt.label}</div>
                  <p className="text-xs text-content-tertiary">{opt.description}</p>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label htmlFor="add-skill-instructions" className="block text-sm font-medium text-content-secondary mb-1.5">
              Instructions <span className="text-red-400">*</span>
            </label>
            <textarea
              id="add-skill-instructions"
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              placeholder="Enter the skill instructions..."
              className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 resize-none min-h-[200px]"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <IconPicker value={icon} onChange={setIcon} label="Icon" />
            <ColorPicker value={color} onChange={setColor} label="Color" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="add-skill-category" className="block text-sm font-medium text-content-secondary mb-1.5">
                Category
              </label>
              <input
                id="add-skill-category"
                type="text"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder="e.g. Research, Writing"
                className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50"
              />
            </div>

            <div>
              <label htmlFor="add-skill-tags" className="block text-sm font-medium text-content-secondary mb-1.5">
                Tags
              </label>
              <input
                id="add-skill-tags"
                type="text"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                placeholder="tag1, tag2, tag3"
                className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50"
              />
              <p className="text-xs text-content-quaternary mt-1">Comma-separated</p>
            </div>
          </div>

          <div className="flex items-center gap-3 pt-4 border-t border-border-primary">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-surface-secondary hover:bg-interactive-active text-content-primary rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2 bg-brand-gradient text-white rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" />
                  Adding...
                </>
              ) : (
                <>
                  <Zap className="w-4 h-4 inline-block mr-2" />
                  Add Skill
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
